import React from "react";
import PropTypes from "prop-types";
import VmoContainer from "../Elements/VmoContainer";
import VmoImage from "../Elements/VmoImage/VmoImage";
import VmoForm from "../Collections/VmoForm/VmoForm";
import VmoFormInput from "../Collections/VmoForm/VmoFormInput";
import VmoButton from "../Elements/VmoButton/VmoButton";
import VmoCard from "../Views/VmoCard/VmoCard";
import VmoCardContent from "../Views/VmoCard/VmoCardContent";
import { Controller, useForm } from "react-hook-form";
import { MdLanguage } from "react-icons/md";
import { joiResolver } from "@hookform/resolvers/joi";
import { verifyDomainSchema } from "../Validation/common.schema";
import VmoBrandLogo from "../Custom/VmoBrandLogo";

export default function VmoDomainVerification({
  header,
  subheader,
  domainProps,
  onDomainVerify = () => null,
  onForgotDomain = () => null,
  onSignUp = () => null,
  signUpUrl = "/signup",
  testIds = {},
}) {
  const { control, handleSubmit } = useForm({
    mode: "onTouched",
    shouldFocusError: true,
    reValidateMode: "onChange",
    submitFocusError: true,
    shouldUnregister: false,
    resolver: joiResolver(verifyDomainSchema),
    defaultValues: {
      domain: "",
    },
  });
  //  This is for testing
  return (
    <VmoContainer data-testid={testIds.container}>
      <div className="signInPage">
        <VmoBrandLogo className="veemo-logo" data-testid={testIds.brandLogo} />
        <div className="signInBody">
          <VmoCard className="loginCard">
            <VmoCardContent>
              <h1>{header}</h1>
              <p>{subheader}</p>
              <VmoForm
                onSubmit={handleSubmit(onDomainVerify)}
                data-testid={testIds.form}
              >
                <Controller
                  name="domain"
                  control={control}
                  render={({
                    field: { value, onChange },
                    fieldState: { error },
                  }) => (
                    <VmoFormInput
                      type="text"
                      fluid
                      className="domain-postfix"
                      icon={<MdLanguage />}
                      iconPosition="left"
                      action={{
                        color: domainProps.color,
                        content: domainProps.content,
                      }}
                      placeholder="Domain"
                      value={value}
                      onChange={(e) => onChange(e.target.value)}
                      error={error && { content: error.message }}
                      data-testid={testIds.domainInput}
                    />
                  )}
                />
                <VmoButton
                  primary
                  type="submit"
                  fluid
                  size="large"
                  data-testid={testIds.verifyButton}
                >
                  Verify
                </VmoButton>
              </VmoForm>
              <div
                className="remMe pt-0"
                style={{ justifyContent: "flex-end" }}
              >
                <span
                  className="mt-3"
                  onClick={onForgotDomain}
                  data-testid={testIds.forgotDomain}
                >
                  Forgot Domain?
                </span>
              </div>
            </VmoCardContent>
          </VmoCard>
        </div>
        <p className="vmo-lead mt-4 cursor-pointer">
          Don't have an account?{" "}
          <a
            href={signUpUrl}
            onClick={onSignUp}
            data-testid={testIds.signUpLink}
            target="_self"
          >
            Sign up
          </a>
        </p>
      </div>
    </VmoContainer>
  );
}

VmoDomainVerification.propTypes = {
  header: PropTypes.string,
  subheader: PropTypes.string,
  domainProps: PropTypes.shape({
    content: PropTypes.string,
    color: PropTypes.string,
  }),
  onDomainVerify: PropTypes.func,
  onForgotDomain: PropTypes.func,
  onSignUp: PropTypes.func,
  signUpUrl: PropTypes.string.isRequired,
  testIds: PropTypes.shape({
    container: PropTypes.string,
    brandLogo: PropTypes.string,
    form: PropTypes.string,
    domainInput: PropTypes.string,
    verifyButton: PropTypes.string,
    forgotDomain: PropTypes.string,
    signUpLink: PropTypes.string,
  }),
};

VmoDomainVerification.defaultProps = {
  header: "Log in to your Veemo account.",
  subheader:
    "Let’s get you all set up so you can verify your nonprofit and begin setting up your first campaign!",
  domainProps: {
    content: ".veemosupport.xyz",
    color: "blue",
  },
  onDomainVerify: () => null,
  onForgotDomain: () => null,
  onSignUp: () => null,
  signUpUrl: "/signup",
  testIds: {
    container: "container",
    brandLogo: "brand-logo",
    form: "form",
    domainInput: "domain-input",
    verifyButton: "verify-button",
    forgotDomain: "forgot-domain",
    signUpLink: "sign-up-link",
  },
};
